/* === BEGIN imported: constants/storageKeys.js (verbatim from plugin) === */
/**
 * SessionStorage Key Constants
 * Centralizes all storage keys to prevent typos and enable easy refactoring
 */

export const STORAGE_KEYS = {
  // ===== DESTINATIONS & ROUTE =====
  DESTINATIONS: 'secondBannerDestinations',
  ROUTE_HASH: 'hopnTripRouteHash',
  ROUTE_BREAKDOWN: 'hopnTripRouteBreakDown',
  MAP_UI: 'hopn-mapUi',
  BANNER_VIEW_TYPE: 'secondBannerViewType',

  // ===== API DATA CACHE =====
  ALL_CARS_API: 'hopnAllCarsApiData',
  LANGUAGE_ARRAY: 'hopnLanguageArray',

  // ===== BOOKING DETAILS =====
  RIDE_DATE: 'hopnRideDate',
  RIDE_TIME: 'hopnRideTime',
  SELECTED_CAR: 'hopnPassengerCar',
  PHONE_NUMBER: 'hopnPassengerPhonerNumber',
  FLIGHT_NUMBER: 'hopnFlightNumber',
  FLIGHT_NUMBER_DETAILS: 'hopnFlightNumberDetails',
  SELECTED_FLIGHT: 'selectedFlight',
  SMART_TIME_FLIGHT_DETAILS: 'hopnSmartTimeFlightDetails',

  // Recommended-pickup flow (travelingDateTime → smartPickupRecommendation →
  // smartPickupResult). FLIGHT_TIME is the departure/arrival time typed on the
  // "I know my time" path (no lookup); FLIGHT_TYPE ('International' | 'Domestic')
  // picks the airport arrival buffer; SCROLL_PICKER = '1' tells travelingDateTime
  // to scroll to the wheel on mount after a "Set Pickup Time Manually".
  SMART_PICKUP_FLIGHT_TIME: 'hopnSmartPickupFlightTime',
  SMART_PICKUP_FLIGHT_TYPE: 'hopnSmartPickupFlightType',
  SMART_PICKUP_SCROLL_PICKER: 'hopnSmartPickupScrollPicker',

  // ===== AIRPORT DETAILS =====
  AIRPORT_DETAILS: 'selectedAirportDetails',
  DEPARTURE_AIRPORT: 'userDepartureAirport',

  // ===== SPECIAL REQUESTS =====
  SPECIAL_REQUEST: 'hopnTripSpacialRequest',
  CARING_PET: 'hopnCaringPet',
  QUIET_RIDE: 'hopnQuietRide',
  CAR_SPECIAL_REQUEST: 'hopnCarSpacialRequest',

  // ===== COUPON & DISCOUNT =====
  COUPON_VALUE: 'hopnCouponValue',
  COUPON_DATA: 'HopnClientCouponData',
  DRIVER_TIP: 'hopnDriverTip',

  // ===== USER DATA =====
  AUTH_TOKEN: 'hopn_login_token',
  USER_EMAIL: 'hopnUserEmail',
  USER_PHONE: 'hopnUserPhoneNumber',
  FIRST_NAME: 'hopnFirstName',
  LAST_NAME: 'hopnLastName',
  // completeProfile step: the fields the booking gate still wants + where to go after.
  IDENTITY_MISSING: 'hopnIdentityMissing',
  IDENTITY_NEXT_STEP: 'hopnIdentityNextStep',
  // ===== PAYMENT =====
  USER_CARD_ID: 'hopnUserCardId',
  USER_SAVED_CARDS: 'hopnUserSavedCards',
  CARD_TOKEN: 'hopn-card-token',
  PAYMENT_DETAILS_POLICIES: 'paymentDetailsPolicies',
  USE_WALLET_AMOUNT: 'hopnUseWalletAmount',

  // ===== MODAL/UI STATE =====
  LATEST_STEP: 'hopn_latestStep',
  EDIT_STEP: 'hopnEditStep',
  PREVIOUS_STEP: 'hopnPreviousStep',
  HAS_USER_CLOSE_POPUP: 'hopn_hasUserClosePopup',
  HAS_USER_CLOSE_BANNER_POPUP: 'hopn_hasUserCloseBannerPopup',
  PHONE_POPUP_DISMISSED: 'hopnPhonePopupDismissed',
  SUMMARY_MESSAGE: 'summary_message',
  SKIP_LUGGAGE_STEP: 'skipLuggageStep',
  PRICING_FINGERPRINT: 'hopnPricingFingerprint',
  ORDER_DETAILS_SCROLL: 'hopnOrderDetailsScroll',
  // Which tab the Cancellation/Modification/Refund popup opens on. A caller (e.g. the
  // "Refund" policies row) stamps 'refund' just before opening; the CMR shell reads it
  // once and clears it, so a later open defaults back to 'cancellation'.
  CMR_INITIAL_TAB: 'hopnCmrInitialTab',
  // PREVIOUS_STEP (above) is the single unified back-nav breadcrumb — it replaced
  // the former POLICIES_ORIGIN key. Auto-stamped by the banner's setStep wrapper;
  // children read it via props.previousStep.

  // ===== BOOKING STATE =====
  // (The created-trip response moved to in-memory state/bookingResult.js —
  // the old CREATED_TRIP_DATA sessionStorage key is gone.)
  RETURN_BOOKING: 'hopnReturnBooking',
  CANCELLED_TRIP_ID: 'hopnCancelledTripId',
  RETURN_CONFETTI_SHOWN: 'hopnReturnConfettiShown',
  SMART_TIME_SELECTED: 'hopnSmartTimeSelected',
  SMART_TIME_SECTION: 'hopnSmartTimeSection',
  TDT_SHOW_WAITING_INFO: 'hopn_tdtShowWaitingInfo',

  // ===== REGISTRATION FORM (clientLoginForms) =====
  LOGIN_EMAIL: 'hopn_login_email',
  LOGIN_PASSWORD: 'hopn_login_password',
  REG_PHONE_NUMBER: 'hopn_phoneNumber',
  REG_FIRST_NAME: 'hopn_first_name',
  REG_LAST_NAME: 'hopn_last_name',
  REG_EMAIL: 'hopn_email',
  REG_PASSWORD: 'hopn_password',
  REG_PASSWORD_CONFIRM: 'hopn_password_confirmation',
  REG_ADDRESS_1: 'hopn_address_1',
  REG_ADDRESS_2: 'hopn_address_2',
  REG_CITY: 'hopn_city',
  REG_STATE: 'hopn_state',
  REG_ZIP_CODE: 'hopn_zip_code',
  REG_COUNTRY: 'hopn_country',
  REG_WHATSAPP: 'hopn_whatsapp',
  REG_PROFILE_IMAGE: 'hopn_profile_image',

  // ===== OTP =====
  OTP_METHOD: 'hopnOtpMethod',
  OTP_SENT_TO: 'hopnOtpSentTo',
  OTP_TIMER_END: 'hopnOtpTimerEnd',
};

/**
 * Counter types used with dynamic counter keys
 */
export const COUNTER_TYPES = {
  PASSENGER_TYPE: 'passengerType',
  CAR_SEAT: 'carSeat',
  STANDARD_LUGGAGE: 'standardLuggage',
  OVERSIZED_LUGGAGE: 'oversizedLuggage',
};

/**
 * Generate a counter key for dynamic items
 * @param {string} type - The counter type from COUNTER_TYPES
 * @param {string|number} id - The item ID
 * @returns {string} - The full storage key
 */
export function getCounterKey(type, id) {
  return `hopn-counter-${type}-${id}`;
}

/**
 * All possible counter key prefixes for cleanup operations
 */
export const COUNTER_KEY_PREFIXES = [
  'hopn-counter-passengerType-',
  'hopn-counter-carSeat-',
  'hopn-counter-standardLuggage-',
  'hopn-counter-oversizedLuggage-',
];

/**
 * Keys cleared when dismissing/closing the booking flow (e.g. Cancel Ride, No thanks, Return Booking redirect)
 * Includes everything EXCEPT persistent user data (PERSISTENT_KEYS) and registration form keys.
 */
export const DISMISS_KEYS = [
  // Destinations & Route
  STORAGE_KEYS.DESTINATIONS,
  STORAGE_KEYS.ROUTE_HASH,
  STORAGE_KEYS.ROUTE_BREAKDOWN,
  STORAGE_KEYS.MAP_UI,
  STORAGE_KEYS.BANNER_VIEW_TYPE,

  // Booking Details
  STORAGE_KEYS.RIDE_DATE,
  STORAGE_KEYS.RIDE_TIME,
  STORAGE_KEYS.SELECTED_CAR,
  STORAGE_KEYS.PHONE_NUMBER,
  STORAGE_KEYS.FLIGHT_NUMBER,
  STORAGE_KEYS.FLIGHT_NUMBER_DETAILS,
  STORAGE_KEYS.SELECTED_FLIGHT,
  STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS,
  STORAGE_KEYS.SMART_PICKUP_FLIGHT_TIME,
  STORAGE_KEYS.SMART_PICKUP_FLIGHT_TYPE,
  STORAGE_KEYS.SMART_PICKUP_SCROLL_PICKER,

  // Airport Details
  STORAGE_KEYS.AIRPORT_DETAILS,
  STORAGE_KEYS.DEPARTURE_AIRPORT,

  // Special Requests
  STORAGE_KEYS.SPECIAL_REQUEST,
  STORAGE_KEYS.CARING_PET,
  STORAGE_KEYS.QUIET_RIDE,
  STORAGE_KEYS.CAR_SPECIAL_REQUEST,

  // Coupon & Discount
  STORAGE_KEYS.COUPON_VALUE,
  STORAGE_KEYS.COUPON_DATA,
  STORAGE_KEYS.DRIVER_TIP,

  // Payment (selection, not saved cards)
  STORAGE_KEYS.USER_CARD_ID,
  STORAGE_KEYS.CARD_TOKEN,
  STORAGE_KEYS.PAYMENT_DETAILS_POLICIES,
  STORAGE_KEYS.USE_WALLET_AMOUNT,

  // Modal/UI State
  STORAGE_KEYS.LATEST_STEP,
  STORAGE_KEYS.EDIT_STEP,
  STORAGE_KEYS.PREVIOUS_STEP,
  STORAGE_KEYS.HAS_USER_CLOSE_POPUP,
  STORAGE_KEYS.HAS_USER_CLOSE_BANNER_POPUP,
  STORAGE_KEYS.PHONE_POPUP_DISMISSED,
  STORAGE_KEYS.SUMMARY_MESSAGE,
  STORAGE_KEYS.SKIP_LUGGAGE_STEP,
  STORAGE_KEYS.PRICING_FINGERPRINT,

  // Booking State
  STORAGE_KEYS.RETURN_BOOKING,
  STORAGE_KEYS.CANCELLED_TRIP_ID,
  STORAGE_KEYS.RETURN_CONFETTI_SHOWN,
  STORAGE_KEYS.SMART_TIME_SELECTED,
  STORAGE_KEYS.SMART_TIME_SECTION,
  STORAGE_KEYS.ORDER_DETAILS_SCROLL,
  STORAGE_KEYS.OTP_METHOD,
  STORAGE_KEYS.OTP_SENT_TO,
  STORAGE_KEYS.OTP_TIMER_END,
];

export default STORAGE_KEYS;
/* === END imported: constants/storageKeys.js === */
